import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { componentKey } from '@entities/search-session';

import styles from './MatchInsights.module.css';
import type { ScoreComponentBreakdown } from './types';

interface ScoreBreakdownProps {
  components: readonly ScoreComponentBreakdown[];
}

/**
 * Разбор Match Score по компонентам (FE-083).
 *
 * Строка показывает балл компонента, его долю в модели после нормализации весов
 * и вклад в итог. Неприменимый компонент остаётся в таблице **без балла**:
 * скрыть его значило бы создать впечатление, что модель применена целиком, а
 * подставить 50 или 100 запрещено прямо (MATCHING §7, §8.1).
 *
 * Числа приходят с backend посчитанными: фронт ничего не умножает и не
 * нормализует (CLAUDE.md §3 п. 4).
 */
export function ScoreBreakdown({ components }: ScoreBreakdownProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const locale = i18n.resolvedLanguage ?? 'de';
  // Вклад компонента — доли балла: округление до целого превращало бы
  // «4,07» в «4» и ломало бы проверку суммы по столбцу.
  const decimal = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });

  if (components.length === 0) return null;

  return (
    <section className={styles.breakdown} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.groupTitle}>
        {t('results.detail.breakdown.title')}
      </h3>
      <p className={styles.breakdownLead}>{t('results.detail.breakdown.lead')}</p>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t('results.detail.breakdown.component')}</th>
              <th scope="col">{t('results.detail.breakdown.score')}</th>
              <th scope="col">{t('results.detail.breakdown.weight')}</th>
              <th scope="col">{t('results.detail.breakdown.contribution')}</th>
            </tr>
          </thead>
          <tbody>
            {components.map((component) => (
              <tr
                key={component.component_code}
                className={component.applicable ? undefined : styles.rowInactive}
              >
                <th scope="row">{t(componentKey(component.component_code))}</th>
                <td className="tabular">
                  {component.raw_score === null ? (
                    <span className={styles.noValue}>
                      {t('results.detail.breakdown.notScored')}
                    </span>
                  ) : (
                    decimal.format(component.raw_score)
                  )}
                </td>
                <td className="tabular">
                  {component.applicable ? `${decimal.format(component.effective_weight)} %` : '—'}
                </td>
                <td className="tabular">
                  {component.applicable ? decimal.format(component.weighted_score) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
